import { MantineColorsTuple } from "@mantine/core";

/**
 * Generates a MantineColorsTuple (10 shades) from a single hex color.
 * This is a simplified version of Mantine's color generation logic.
 */
export const generateMantineColors = (baseColor: string): MantineColorsTuple => {
  // If baseColor is invalid hex, return a fallback blue palette
  if (!/^#([0-9A-F]{3}){1,2}$/i.test(baseColor)) {
    return [
      "#eef2ff", "#e0e7ff", "#c7d2fe", "#a5b4fc", "#818cf8",
      "#6366f1", "#4f46e5", "#4338ca", "#3730a3", "#312e81"
    ] as MantineColorsTuple;
  }

  // Convert hex to RGB
  const hex = baseColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Convert RGB to HSL
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;
  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  let h = 0, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rNorm: h = (gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0); break;
      case gNorm: h = (bNorm - rNorm) / d + 2; break;
      case bNorm: h = (rNorm - gNorm) / d + 4; break;
    }
    h /= 6;
  }

  h = Math.round(h * 360);
  s = Math.round(s * 100);
  // l is already normalized

  // Define 10 lightness levels for Mantine
  // Shades 0-9: from very light to very dark
  // Typically: 96, 90, 84, 75, 65, 54, 45, 38, 32, 25
  const lightnessLevels = [97, 91, 82, 72, 62, 52, 43, 35, 28, 21];

  return lightnessLevels.map(lightness => {
    // Helper to convert HSL to Hex
    const hDecimal = h / 360;
    const sDecimal = s / 100;
    const lDecimal = lightness / 100;

    let rFinal, gFinal, bFinal;
    if (sDecimal === 0) {
      rFinal = gFinal = bFinal = lDecimal;
    } else {
      const q = lDecimal < 0.5 ? lDecimal * (1 + sDecimal) : lDecimal + sDecimal - lDecimal * sDecimal;
      const p = 2 * lDecimal - q;
      const hueToRgb = (pVal: number, qVal: number, tVal: number) => {
        if (tVal < 0) tVal += 1;
        if (tVal > 1) tVal -= 1;
        if (tVal < 1 / 6) return pVal + (qVal - pVal) * 6 * tVal;
        if (tVal < 1 / 2) return qVal;
        if (tVal < 2 / 3) return pVal + (qVal - pVal) * (2 / 3 - tVal) * 6;
        return pVal;
      };
      rFinal = hueToRgb(p, q, hDecimal + 1 / 3);
      gFinal = hueToRgb(p, q, hDecimal);
      bFinal = hueToRgb(p, q, hDecimal - 1 / 3);
    }

    const toHex = (x: number) => {
      const hexVal = Math.round(x * 255).toString(16);
      return hexVal.length === 1 ? '0' + hexVal : hexVal;
    };
    return `#${toHex(rFinal)}${toHex(gFinal)}${toHex(bFinal)}`;
  }) as unknown as MantineColorsTuple;
};
